import { Component, computed, inject, input, output } from '@angular/core';
import { ActionItemInterface, ConversationInterface, TopicInterface } from '@vendasta/meeting-analysis';
import { CommonModule, KeyValue } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { LexiconModule } from '@galaxy/lexicon';
import { ParticipantComponent } from '../../participants/participant/participant.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { copyToClipboard } from '../../../utils/copy';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { VideoJumpComponent } from '../video-jump/video-jump.component';

@Component({
  selector: 'meeting-analysis-summary',
  styleUrls: ['./summary.component.scss'],
  templateUrl: './summary.component.html',
  imports: [
    CommonModule,
    MatCardModule,
    LexiconModule,
    ParticipantComponent,
    MatExpansionModule,
    MatIconModule,
    VideoJumpComponent,
  ],
})
export class SummaryComponent {
  private translate = inject(TranslateService);
  private snackbarService = inject(SnackbarService);
  conversation = input.required<ConversationInterface>();
  readonly timeStampSelected = output<number>();

  actionItemsByOwner = computed(() => {
    const actionItems = this.conversation()?.actionItemsV2 || [];
    const grouped = new Map<string, any[]>();

    actionItems.forEach((actionItem) => {
      const unknownOwner = this.translate.instant('COMMON.UNKNOWN');
      // Extract owner name - adjust this based on your actual data structure
      const ownerName = actionItem.owner || unknownOwner;

      if (!grouped.has(ownerName)) {
        grouped.set(ownerName, []);
      }
      grouped.get(ownerName)!.push(actionItem);
    });
    return grouped;
  });

  getDuration(duration: number | undefined): string {
    if (!duration) return '(0 min)';

    const totalSeconds = Math.round(duration);

    if (totalSeconds >= 60) {
      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;

      if (remainingSeconds < 30) {
        return `(${minutes} min)`;
      } else {
        return `(${minutes + 1} min)`;
      }
    } else {
      return `(1 min)`;
    }
  }

  copyOverview(): void {
    copyToClipboard(this.conversation()?.summary || '');
    this.snackbarService.openSuccessSnack('COMMON.COPIED');
  }

  copyActionItems(): void {
    const actionItemsFor = this.translate.instant('ACTION_ITEMS_FOR');
    let textToCopy = '';
    this.actionItemsByOwner().forEach((items, owner) => {
      const actionItemsText = items.map((item) => `- ${item.item}`).join('\n');
      textToCopy = textToCopy + `${actionItemsFor} ${owner}:\n${actionItemsText}\n`;
    });
    copyToClipboard(textToCopy);
    this.snackbarService.openSuccessSnack('COMMON.COPIED');
  }

  copyActionItem(item: KeyValue<string, ActionItemInterface[]>): void {
    const actionItemsFor = this.translate.instant('ACTION_ITEMS_FOR');
    let textToCopy = '';
    const actionItemsText = item.value.map((item) => `- ${item.item}`).join('\n');
    textToCopy = textToCopy + `${actionItemsFor} ${item.key}:\n${actionItemsText}\n`;
    copyToClipboard(textToCopy);
    this.snackbarService.openSuccessSnack('COMMON.COPIED');
  }

  copyTopics(): void {
    let textToCopy = '';
    (this.conversation()?.topicsDiscussed || []).forEach((topic) => {
      const keyTakeawayText = (topic.keyTakeaways || [])
        .map((keyTakeaway) => `- ${keyTakeaway.keyTakeaway}`)
        .join('\n');
      textToCopy = textToCopy + `${topic.topic}:\n${keyTakeawayText}\n`;
    });
    copyToClipboard(textToCopy);
    this.snackbarService.openSuccessSnack('COMMON.COPIED');
  }

  copyTopic(topic: TopicInterface): void {
    let textToCopy = '';
    const keyTakeawayText = (topic.keyTakeaways || []).map((keyTakeaway) => `- ${keyTakeaway.keyTakeaway}`).join('\n');
    textToCopy = textToCopy + `${topic.topic}:\n${keyTakeawayText}\n`;
    copyToClipboard(textToCopy);
    this.snackbarService.openSuccessSnack('COMMON.COPIED');
  }

  clickedTimestamp(timestamp: number): void {
    // Jump slightly before what was selected so it's a more natural start
    if (timestamp > 0.5) {
      timestamp = timestamp - 0.5;
    }
    this.timeStampSelected.emit(timestamp);
  }
}
